<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-12">
                <soft-alert
                    v-if="alertTable.state"
                    :color="alertTable.color"
                    class="mb-3"
                    dismissible
                >
                    <span class="text-sm">{{ alertTable.message }}</span>
                </soft-alert>
            </div>

            <div v-if="sinAcceso" class="col-12">
                <div class="crm-panel text-center sin-acceso">
                    <i class="fas fa-lock mb-3"></i>
                    <h5 class="font-weight-bolder mb-2">Sin acceso</h5>
                    <p class="text-sm text-secondary mb-0">
                        Solo el administrador principal de la plataforma gestiona estos roles.
                    </p>
                </div>
            </div>

            <div v-else class="col-12">
                <div class="crm-panel mb-3">
                    <div class="d-flex flex-wrap gap-2 align-items-center">
                        <div class="me-auto">
                            <h6 class="mb-1 text-dark">Roles de plataforma</h6>
                            <p class="text-xs text-muted mb-0">
                                Todos estos roles dan acceso total al CRM. El nombre sirve para distinguirlos
                                y es el que aparece en la auditoría.
                            </p>
                        </div>
                        <soft-button variant="outline" color="secondary" size="sm" @click="getRoles">
                            <i class="fas fa-sync-alt me-1"></i>
                            Actualizar
                        </soft-button>
                        <soft-button variant="gradient" color="primary" size="sm" @click="abrirForm()">
                            <i class="fas fa-plus me-1"></i>
                            Nuevo rol
                        </soft-button>
                    </div>
                </div>

                <div v-if="spinner" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                </div>

                <div v-else-if="!roles.length" class="crm-panel text-center text-muted py-5 small">
                    <i class="fas fa-inbox d-block mb-2" style="font-size: 1.5rem; color: #cbd5e1"></i>
                    No hay roles de plataforma
                </div>

                <div v-else class="row g-3">
                    <div v-for="rol in roles" :key="rol.id" class="col-xl-4 col-md-6">
                        <div class="crm-panel h-100 d-flex flex-column">
                            <div class="d-flex align-items-start gap-2 mb-2">
                                <div class="me-auto min-w-0">
                                    <h6 class="mb-1 text-dark text-break">{{ rol.nombre }}</h6>
                                    <div class="d-flex flex-wrap gap-1">
                                        <span v-if="rol.es_base" class="badge bg-primary" title="De este rol salen los permisos de todos los admins">
                                            Base
                                        </span>
                                        <span
                                            v-if="!rol.acceso_total"
                                            class="badge bg-warning text-dark"
                                            title="Pasa los chequeos de super admin, pero el login no le da el menú completo"
                                        >
                                            Rol antiguo · menú parcial
                                        </span>
                                    </div>
                                </div>
                                <a href="#" class="table-action-link" title="Editar" @click.prevent="abrirForm(rol)">
                                    <i class="fas fa-edit"></i>
                                </a>
                                <a
                                    v-if="!rol.es_base"
                                    href="#"
                                    class="table-action-link text-danger"
                                    :class="{ disabled: rol.usuarios?.length }"
                                    :title="rol.usuarios?.length ? 'Quita primero a sus usuarios' : 'Eliminar'"
                                    @click.prevent="!rol.usuarios?.length && abrirEliminar(rol)"
                                >
                                    <i class="fas fa-trash"></i>
                                </a>
                            </div>

                            <p v-if="rol.descripcion" class="text-xs text-secondary mb-2">{{ rol.descripcion }}</p>

                            <div class="usuarios-lista flex-grow-1">
                                <div class="text-xxs text-uppercase fw-bold text-muted mb-1">
                                    Usuarios ({{ rol.usuarios?.length || 0 }})
                                </div>
                                <div v-if="!rol.usuarios?.length" class="text-xs text-muted fst-italic">
                                    Sin usuarios
                                </div>
                                <div
                                    v-for="usuario in rol.usuarios"
                                    :key="usuario.id"
                                    class="usuario-item d-flex align-items-center gap-2"
                                >
                                    <div class="me-auto min-w-0">
                                        <div class="text-sm text-dark text-truncate">{{ usuario.email }}</div>
                                        <div v-if="usuario.nombre" class="text-xxs text-muted text-truncate">
                                            {{ usuario.nombre }}
                                        </div>
                                    </div>
                                    <span v-if="usuario.id === usuarioActualId" class="badge bg-light text-dark border">Tú</span>
                                    <a
                                        v-else
                                        href="#"
                                        class="table-action-link text-danger"
                                        title="Quitar acceso de admin"
                                        @click.prevent="abrirQuitar(rol, usuario)"
                                    >
                                        <i class="fas fa-user-minus"></i>
                                    </a>
                                </div>
                            </div>

                            <soft-button
                                class="mt-3"
                                variant="outline"
                                color="primary"
                                size="sm"
                                full-width
                                @click="abrirAsignar(rol)"
                            >
                                <i class="fas fa-user-plus me-1"></i>
                                Agregar usuario
                            </soft-button>
                        </div>
                    </div>
                </div>

                <!-- Crear / editar -->
                <soft-modal v-if="modalForm" :modalActive="modalForm" size="sm" @close="cerrarForm">
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-user-lock me-2 text-primary"></i>
                            {{ rolSeleccionado ? 'Editar rol' : 'Nuevo rol de plataforma' }}
                        </h5>
                    </template>
                    <form class="p-1" @submit.prevent="guardarRol">
                        <label for="rolNombre" class="form-label fw-semibold text-xs">Nombre</label>
                        <input
                            id="rolNombre"
                            v-model="form.nombre"
                            type="text"
                            class="form-control form-control-sm mb-3"
                            placeholder="Ej. Soporte"
                            maxlength="191"
                        />
                        <label for="rolDescripcion" class="form-label fw-semibold text-xs">Descripción (opcional)</label>
                        <textarea
                            id="rolDescripcion"
                            v-model="form.descripcion"
                            class="form-control form-control-sm"
                            rows="3"
                        ></textarea>
                        <div v-if="formError" class="alert alert-danger py-2 px-3 text-sm mt-3 mb-0">{{ formError }}</div>
                        <div class="d-flex gap-2 justify-content-end mt-3 pt-3 border-top">
                            <soft-button variant="outline" color="secondary" size="sm" type="button" @click="cerrarForm">
                                Cancelar
                            </soft-button>
                            <soft-button variant="gradient" color="primary" size="sm" type="submit" :loading="isLoading">
                                Guardar
                            </soft-button>
                        </div>
                    </form>
                </soft-modal>

                <!-- Eliminar -->
                <soft-modal v-if="modalEliminar" :modalActive="modalEliminar" size="sm" @close="cerrarEliminar">
                    <div class="text-center p-3">
                        <h5 class="font-weight-bolder mb-2">Eliminar rol</h5>
                        <p class="text-sm text-secondary mb-3">
                            ¿Seguro que deseas eliminar el rol <strong>{{ rolSeleccionado?.nombre }}</strong>?
                        </p>
                        <div class="d-flex gap-2 justify-content-center">
                            <soft-button variant="outline" color="secondary" size="sm" @click="cerrarEliminar">
                                Cancelar
                            </soft-button>
                            <soft-button variant="gradient" color="danger" size="sm" :loading="isLoading" @click="confirmarEliminar">
                                <i class="fas fa-trash me-1"></i>
                                Eliminar
                            </soft-button>
                        </div>
                    </div>
                </soft-modal>

                <!-- Asignar usuario -->
                <soft-modal v-if="modalAsignar" :modalActive="modalAsignar" size="md" @close="cerrarModalesUsuario()">
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-user-plus me-2 text-primary"></i>
                            Agregar usuario a {{ rolSeleccionado?.nombre }}
                        </h5>
                    </template>
                    <asignar-usuario-modal
                        :rol="rolSeleccionado"
                        :admins-actuales="adminsActuales"
                        @close="cerrarModalesUsuario()"
                        @asignado="cerrarModalesUsuario"
                    />
                </soft-modal>

                <!-- Quitar usuario -->
                <soft-modal v-if="modalQuitar" :modalActive="modalQuitar" size="sm" @close="cerrarModalesUsuario()">
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-user-minus me-2 text-danger"></i>
                            Quitar acceso de admin
                        </h5>
                    </template>
                    <quitar-usuario-modal
                        :rol="rolSeleccionado"
                        :usuario="usuarioSeleccionado"
                        @close="cerrarModalesUsuario()"
                        @quitado="cerrarModalesUsuario"
                    />
                </soft-modal>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import SoftModal from '@/components/SoftModal.vue';
import SoftButton from '@/components/SoftButton.vue';
import SoftAlert from '@/components/SoftAlert.vue';
import AsignarUsuarioModal from './components/asignarUsuarioModal.vue';
import QuitarUsuarioModal from './components/quitarUsuarioModal.vue';
import { useRolesPlataforma } from './useRolesPlataforma.js';

export default {
    name: 'rolesPlataforma',
    components: {
        SoftModal,
        SoftButton,
        SoftAlert,
        AsignarUsuarioModal,
        QuitarUsuarioModal
    },
    setup() {
        const store = useStore();
        const rolesPlataforma = useRolesPlataforma();

        rolesPlataforma.getRoles();

        const usuarioActualId = computed(() => store.getters['appUserdata/getUser']?.id);

        return { ...rolesPlataforma, usuarioActualId };
    }
};
</script>

<style lang="scss" scoped>
.crm-panel {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    padding: 1.25rem;
}

.sin-acceso {
    padding: 3rem 1.5rem;

    > i {
        font-size: 2rem;
        color: #cbd5e1;
    }
}

.usuarios-lista {
    border-top: 1px solid #f1f5f9;
    padding-top: 0.75rem;
}

.usuario-item {
    padding: 0.35rem 0;
    border-bottom: 1px solid #f8fafc;

    &:last-child {
        border-bottom: none;
    }
}

.min-w-0 {
    min-width: 0;
}

.table-action-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 0.375rem;
    color: #8392ab;
    transition: all 0.15s ease;
    text-decoration: none;

    &:hover {
        background-color: #f0f2f5;
        color: #344767;
    }

    &.text-danger:hover {
        background-color: #fef2f2;
        color: #ea0606;
    }

    &.disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
}

.modal-title {
    color: #344767;
    font-weight: 600;
}
</style>
